import styles from '../globals/pages.module.scss'

import { useEffect, useState } from 'react'
import axios from 'axios'
import { useParams } from 'react-router-dom'

import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { browserName, osName, getUA } from 'react-device-detect'

import { Wrapper, SignaturePad } from '../components/reusable'
import { apiErrors } from '../helpers/apiErrors'
import PocView from '../components/pocView'
import Previewer from '../components/reusable/previewer'

interface ContractData {
    token: string
    recipient_name: string | null
    document_name: string | null
    status: string
    doc_url: string
}

export default function Contract() {
    const [contractData, setContractData] = useState<ContractData | null>(null)
    const [retrieved, setRetrieved] = useState(false)
    const { token } = useParams()

    useEffect(() => {
        if (!retrieved) {
            Loading.standard('Loading...')
            axios
                .post(`${process.env.REACT_APP_API_TOKEN_URL}/contract`, { token })
                .then((response) => {
                    const responseData = response.data
                    if (responseData === 'Contract Complete') {
                        Loading.remove()
                        Report.success('Document Signed', 'This document has already been signed.', 'Okay', () => {
                            window.location.href = 'https://sentinellegal.co.uk'
                        })
                        return
                    }

                    const signatureData = responseData.sigData
                    if (!signatureData) throw new Error(responseData)

                    setContractData(signatureData)
                    setRetrieved(true)
                    Loading.remove()
                })
                .catch((e) => {
                    console.error('error:', e)
                    const serverError = e?.response?.data ?? 'error'
                    Loading.remove()
                    apiErrors(typeof serverError === 'string' ? serverError : 'error')
                })
        }
    }, [])

    const completeContract = () => {
        Report.success(
            'Document Signed',
            'Thank you for signing your document. Sentinel Legal will be in touch in due course.',
            'Okay',
            () => {
                window.location.href = 'https://sentinellegal.co.uk'
            }
        )
    }

    return (
        <div className={`${styles.pageContent} ${styles.pocContent}`}>
            <Wrapper>
                <PocView>
                    {retrieved && contractData ? (
                        <div className={styles.narrowContainer}>
                            <div className={styles.centeredContainer}>
                                <h2 className={styles.headerTitle}>
                                    {contractData.recipient_name
                                        ? `${contractData.recipient_name}, a document requires your signature`
                                        : 'A document requires your signature'}
                                </h2>
                                <span className={styles.headerDivider}></span>
                                <p className={styles.centeredText}>
                                    Sentinel Legal have sent you{' '}
                                    {contractData.document_name ? (
                                        <strong>{contractData.document_name}</strong>
                                    ) : (
                                        'a document'
                                    )}{' '}
                                    to review and sign. Please read the document below, then sign in the signature box
                                    to complete it.
                                </p>
                                <hr />
                            </div>

                            <Previewer
                                url={contractData.doc_url}
                                fullWidth
                                iframeHeight={700}
                                showText="Click here to view your document"
                                hideText="Click here to hide your document"
                            />

                            <SignaturePad
                                additionalAction={completeContract}
                                extraPayload={{
                                    type: 'contract-signature',
                                    metadata: {
                                        browser: browserName,
                                        device: osName,
                                        userAgent: getUA,
                                    },
                                }}
                                agreementLabel="I confirm that I have read and understood the document above, and I authorise Sentinel Legal to apply my signature to it."
                                privacyLabel="I agree to the privacy policy of Sentinel Legal."
                                disclaimer="*By signing, you agree that your signature will be applied to the document shown above at the positions indicated by Sentinel Legal. A record of your signature, including the time, IP address and device used, will be stored securely for verification purposes."
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                </PocView>
            </Wrapper>
        </div>
    )
}
